import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { cn } from "../../common/u1/cn";
import UserProfileDropdown from "../../public/UserProfileHeader";
import { MenuIcon, SearchIcon, BellIcon } from "../../../assets/ui/icons";
import { SearchBar } from "../../public/Search/u1/SearchBar";
import { NotificationDrawer } from "./notifications/NotificationDrawer";

/* The dashboard top bar. Sits on the same cream as the page rather than a white band, so the shell reads as one surface — the design has no seam between bar... */
export const DashboardTopbar = ({
  links = [],
  unreadCount = 0,
  onOpenNav,
  className = "",
}) => {
  const { pathname } = useLocation();
  const activeTab = useSelector((state) => state.activeTab.activeTab);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  /* Phone only: the app bar swaps its contents for the search field. */
  const [searching, setSearching] = useState(false);

  /* Longest matching route wins, so /dashboard/trade-finance is not answered by /dashboard. */
  const matched = links
    .filter((l) =>
      l.link === "/dashboard"
        ? pathname === l.link
        : pathname === l.link || pathname.startsWith(`${l.link}/`)
    )
    .sort((a, b) => b.link.length - a.link.length)[0];
  const title = matched?.name || activeTab || "Dashboard";

  return (
    <header
      className={cn(
        /* Pinned to the top of the scrolling content column. It needs its own
           background — the bar is transparent over the page cream, so without
           one the content would scroll visibly underneath it. */
        /* 60px on a phone, 78px above — the design's app-bar height. */
        "sticky top-0 z-30 flex h-[78px] shrink-0 items-center gap-4 bg-[var(--bg)] px-4 sm:px-6 lg:px-8",
        "max-[743px]:h-[60px] max-[743px]:gap-2 max-[743px]:px-3",
        className
      )}
    >
      <button
        type="button"
        onClick={onOpenNav}
        aria-label="Open navigation"
        className={cn(
          "-ml-1 hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[var(--ink)] hover:bg-black/5",
          searching ? "max-[743px]:hidden" : "max-[743px]:flex"
        )}
      >
        <MenuIcon />
      </button>

      <h1
        className={cn(
          "shrink-0 text-[20px] font-bold tracking-[-0.01em] text-[var(--ink)] font-sans sm:text-[22px]",
          /* On a phone the title takes the slack and clips rather than pushing
             the icons off the row. */
          "max-[743px]:min-w-0 max-[743px]:grow max-[743px]:truncate max-[743px]:text-[16.5px]",
          searching && "max-[743px]:hidden"
        )}
      >
        {title}
      </h1>

      {/* The same control the /search page uses, sized to this row. */}
      <div
        className={cn(
          "min-w-0",
          "min-[744px]:mx-auto min-[744px]:block min-[744px]:w-full min-[744px]:max-w-[700px]",
          searching ? "max-[743px]:block max-[743px]:grow" : "max-[743px]:hidden"
        )}
      >
        <SearchBar
          /* A launcher, not a readout: the app bar follows the viewer around
             the dashboard and must not carry the last search with it. */
          reflectQuery={false} size="sm" autoFocus={searching} />
      </div>

      {/* Phone: the collapsed search, and the way back out of it. */}
      <button
        type="button"
        onClick={() => setSearching(true)}
        aria-label="Open search"
        aria-expanded={searching}
        className={cn(
          "hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[var(--coral)] hover:bg-black/5",
          searching ? "max-[743px]:hidden" : "max-[743px]:flex"
        )}
      >
        <SearchIcon size={20} />
      </button>

      <button
        type="button"
        onClick={() => setSearching(false)}
        aria-label="Close search"
        className={cn(
          "hidden h-11 shrink-0 items-center justify-center px-1 text-[14px] font-bold text-[var(--coral)] font-sans",
          searching && "max-[743px]:flex"
        )}
      >
        Cancel
      </button>

      <div
        className={cn(
          "ml-auto flex shrink-0 items-center gap-3 sm:ml-0",
          "max-[743px]:gap-1.5",
          searching && "max-[743px]:hidden"
        )}
      >
        {/* Unread notifications. The count is the same data the rail's dots
            read. The bell now opens the drawer rather than jumping straight to
            Requests — which is still one click away, from inside it. */}
        <button
          type="button"
          onClick={() => setNotificationsOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={notificationsOpen}
          aria-label={
            unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"
          }
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-white text-[var(--ink)] transition-colors hover:border-[var(--field-border-hover)]"
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--coral)] px-1 text-[11px] font-bold text-white"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        <NotificationDrawer
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
        />

        {/* Kept on a phone, where the public header still hides it — the app
            bar is the only way into the account menu there. */}
        {userInfo && <UserProfileDropdown showDetails className="max-[743px]:flex" />}
      </div>
    </header>
  );
};

export default DashboardTopbar;
